import React, { useEffect, useRef, useState } from 'react'
import { Cog, OctagonAlert, Workflow } from 'lucide-react'
import { useCVProcessing } from 'contexts/CVProcessingContext'
import { cn } from 'lib/utils'
import { useTranslation } from 'react-i18next'

interface CVProcessingIndicatorProps {
  className?: string
}

export function CVProcessingIndicator({ className }: CVProcessingIndicatorProps) {
  const { t } = useTranslation()
  const { cvProcessing } = useCVProcessing()
  const { isProcessing, processingCount, lastProcessedFile, statusMessage, lastError, batchDetails } = cvProcessing

  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    // Auto-close popover when processing completes and there's no error
    if (!isProcessing && !lastError) {
      setIsOpen(false)
    }
    // Auto-close popover after 5 seconds if there's an error (to avoid persistent error display)
    if (lastError && !isProcessing) {
      const timer = setTimeout(() => {
        setIsOpen(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [isProcessing, lastError])

  // Don't show indicator if there's no active processing
  // Only show error if there's active processing (errors during processing are relevant)
  if (!isProcessing) {
    return null
  }

  const renderBadge = (count: number, tone: 'blue' | 'red') => (
    <span
      className={cn(
        'absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white',
        tone === 'red' ? 'bg-red-500' : 'bg-blue-600'
      )}
    >
      {count}
    </span>
  )

  const panelContent = (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        lastError
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-100'
          : 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-100'
      )}
    >
      {lastError ? (
        <OctagonAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
      ) : (
        <Workflow className="mt-0.5 h-5 w-5 flex-shrink-0" />
      )}
      <div className="space-y-1 text-xs">
        <p className="text-sm font-semibold">
          {lastError ? t('candidates.cvProcessingErrorTitle') : statusMessage || t('candidates.processingCV')}
        </p>
        {lastError ? (
          <p>{lastError}</p>
        ) : (
          <>
            {lastProcessedFile && (
              <p>{t('candidates.analyzing', { filename: lastProcessedFile })}</p>
            )}
            {batchDetails ? (
              <div className="mt-2">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="opacity-80">{t('candidates.batchTotal', 'Total')}:</span>
                    <span className="font-semibold">{batchDetails.total}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="opacity-80">{t('candidates.batchProcessed', 'Processed')}:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">{batchDetails.processed}</span>
                  </div>
                  {batchDetails.successful > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-80">{t('candidates.batchSuccessful', 'Successful')}:</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">{batchDetails.successful}</span>
                    </div>
                  )}
                  {batchDetails.failed > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-80">{t('candidates.batchFailed', 'Failed')}:</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">{batchDetails.failed}</span>
                    </div>
                  )}
                  {batchDetails.skipped > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-80">{t('candidates.batchSkipped', 'Skipped')}:</span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">{batchDetails.skipped}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="opacity-80">
                {processingCount > 1
                  ? t('candidates.filesInQueue', { count: processingCount })
                  : t('candidates.stayOnPage')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )

  return (
    <>
      <div className={cn('relative flex items-center', className)}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={cn(
            'relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
            lastError
              ? 'border-red-400 text-red-600 bg-red-50 dark:bg-red-900/40 dark:border-red-800'
              : 'border-blue-400 text-blue-600 bg-blue-50 dark:bg-blue-900/40 dark:border-blue-700'
          )}
          title={
            lastError
              ? t('candidates.cvProcessingErrorTitle')
              : statusMessage || t('candidates.processingCV')
          }
          aria-expanded={isOpen}
        >
          {lastError ? (
            <OctagonAlert className="h-4 w-4" />
          ) : (
            <Cog className="h-4 w-4 animate-spin" />
          )}
          {processingCount > 0 && renderBadge(processingCount, lastError ? 'red' : 'blue')}
        </button>

        {isOpen && (
          <div
            ref={popoverRef}
            className="absolute right-0 top-12 z-50 w-[500px] max-w-md rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl" 
          >
            <div className="px-6 py-4 text-sm">
              {panelContent}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
